import React from 'react';
import { IconButton } from '../Button';
import styled from 'styled-components';
import { IconButtonContentStyles } from '../Button/Iconbutton';
import { COLORS } from '../styles';

const Wrapper = styled.div`
  ${IconButtonContentStyles} {
    transition: all 0.1s ease-in-out;
  }

  &.disabled,
  &.active:not(.state-change) {
    ${IconButtonContentStyles} {
      background: transparent;
    }
  }

  &.active:not(.state-change) {
    ${IconButtonContentStyles}:after {
      content: '';
      position: absolute;
      left: 12px;
      bottom: 1px;
      background-color: ${props => COLORS.generateToken({componentType: 'border', defaultVariant: 'selected'}, props.theme)};
      border-radius: 4px;
      height: 4px;
      width: 24px;
    }
  }

  &.disabled:not(.state-change) {
    ${IconButtonContentStyles}:after {
      background: ${props => COLORS.generateToken({componentType:'icon',state:'disabled'}, props.theme)};
    }
  }
`;

export interface ToggleButtonState {
  icon: React.ReactNode;
  color?: string;
}

export interface ToggleButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'disabled' | 'onClick'> {
  /** Required. Initial state of the toggle. */
  active: boolean;
  /** Optional. Handler to be called when toggle state changes. */
  onChange?: (value: boolean) => void;
  /** Optional. If set user can not interact with ToggleButton. */
  disabled?: boolean;
  /** Required. Icon and color of toggle in default state. */
  defaultState: ToggleButtonState;
  /** Optional. Icon and color of toggle in active state. If activeState is not provided then when state is changed component will show a small border under the button to highlight the state change. */
  activeState?: ToggleButtonState;
}

export const ToggleButton = React.forwardRef(({
                                                id,
                                                active,
                                                onChange,
                                                disabled,
                                                defaultState,
                                                activeState,
                                                className,
                                                ...rest
                                              }: ToggleButtonProps, ref: React.Ref<HTMLButtonElement>) => {
  const [isActive, setIsActive] = React.useState(false);

  React.useEffect(() => setIsActive(active), [active]);

  const cls = `${className || ''} ${isActive ? 'active' : ''} ${activeState ? 'state-change' : ''} ${disabled ? 'disabled' : ''}`;

  return (
    <Wrapper className={cls}>
      <IconButton
        variant={'secondary'}
        ref={ref}
        useTransparentBackground={true}
        shape={'circular'}
        action={() => {
          setIsActive(!isActive);
          if (onChange) {
            onChange(!isActive);
          }
        }}
        id={id}
        iconColor={isActive ? activeState?.color ?? defaultState.color : defaultState.color}
        disabled={disabled}
        aria-pressed={isActive}
        {...rest}>
        {isActive ? activeState?.icon ?? defaultState.icon : defaultState.icon}
      </IconButton>
    </Wrapper>
  );
});

export default ToggleButton;
